import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { FaSync } from 'react-icons/fa';
import { toast } from 'react-toastify';
import Spinner from './Spinner';
import isAdministrator from '../utilities/checkPrivileges.js';
import {
  getDdmStatus,
  getEnrollmentDdmSummary,
  enableEnrollmentDdm,
  notifyEnrollmentDdm,
  clearEnrollmentStatusErrors,
} from '../services/nanoDdmService.js';

function asList(value) {
  if (Array.isArray(value)) return value;
  if (value == null) return [];
  return [value];
}

function pickDeclarations(summary, udid) {
  const raw = summary?.declarations;
  if (Array.isArray(raw)) return raw;
  if (raw && typeof raw === 'object') {
    if (udid && Array.isArray(raw[udid])) return raw[udid];
    const first = Object.values(raw).find(Array.isArray);
    return first || [];
  }
  return [];
}

function pickErrors(summary, udid) {
  const raw = summary?.errors;
  if (Array.isArray(raw)) return raw;
  if (raw && typeof raw === 'object') {
    if (udid && Array.isArray(raw[udid])) return raw[udid];
    const first = Object.values(raw).find(Array.isArray);
    return first || [];
  }
  return [];
}

function formatReasons(reasons) {
  const list = asList(reasons).filter(Boolean);
  if (!list.length) return '—';
  return list
    .map((item) => {
      if (typeof item === 'string') return item;
      return item.description || item.Description || item.code || item.Code || JSON.stringify(item);
    })
    .join('; ');
}

function formatTimestamp(value) {
  if (!value) return '—';
  const ms = typeof value === 'number' ? value : Date.parse(value);
  if (!Number.isFinite(ms)) return String(value);
  return new Date(ms).toLocaleString();
}

function syncBadge(decl) {
  if (!decl || decl.pending) {
    return { label: 'Wartet auf Check-in', className: 'text-bg-secondary' };
  }
  const valid = String(decl?.valid || '').toLowerCase();
  if (valid === 'invalid') {
    return { label: 'Fehler', className: 'text-bg-danger' };
  }
  if (decl?.current && valid === 'valid') {
    return { label: 'Synchron', className: 'text-bg-success' };
  }
  if (decl?.current === false) {
    return { label: 'Nicht aktuell', className: 'text-bg-warning' };
  }
  return { label: valid || 'unbekannt', className: 'text-bg-secondary' };
}

function errorText(error) {
  if (!error) return '—';
  if (typeof error === 'string') return error;
  return error.description || error.Description || error.code || error.Code || JSON.stringify(error);
}

const STATUS_GROUPS = [
  {
    title: 'Management',
    keys: [
      'management.declarations',
      'management.client-capabilities',
    ],
  },
  {
    title: 'Gerät',
    keys: [
      'device.identifier.serial-number',
      'device.identifier.udid',
      'device.model.marketing-name',
      'device.model.identifier',
      'device.model.family',
      'device.model.number',
    ],
  },
  {
    title: 'Betriebssystem',
    keys: [
      'device.operating-system.marketing-name',
      'device.operating-system.family',
      'device.operating-system.version',
      'device.operating-system.build-version',
      'device.operating-system.supplemental.build-version',
      'device.operating-system.supplemental.extra-version',
    ],
  },
  {
    title: 'Software-Update',
    keys: [
      'softwareupdate.install-state',
      'softwareupdate.pending-version',
      'softwareupdate.install-reason',
      'softwareupdate.failure-reason',
      'softwareupdate.device-id',
      'softwareupdate.beta-enrollment',
    ],
  },
  {
    title: 'Sicherheit',
    keys: [
      'passcode.is-present',
      'passcode.is-compliant',
      'device.power.battery-health',
    ],
  },
];

const STATUS_LABELS = {
  'management.declarations': 'Deklarationsstatus',
  'management.client-capabilities': 'Client-Capabilities',
  'device.identifier.serial-number': 'Seriennummer',
  'device.identifier.udid': 'UDID',
  'device.model.family': 'Modellfamilie',
  'device.model.identifier': 'Modellkennung',
  'device.model.marketing-name': 'Modellname',
  'device.model.number': 'Modellnummer',
  'device.operating-system.family': 'OS-Familie',
  'device.operating-system.version': 'OS-Version',
  'device.operating-system.build-version': 'Build',
  'device.operating-system.marketing-name': 'OS-Name',
  'device.operating-system.supplemental.build-version': 'Supplemental Build',
  'device.operating-system.supplemental.extra-version': 'Rapid Security Response',
  'softwareupdate.device-id': 'Update Device-ID',
  'softwareupdate.pending-version': 'Anstehendes Update',
  'softwareupdate.install-state': 'Update-Status',
  'softwareupdate.install-reason': 'Update-Auslöser',
  'softwareupdate.failure-reason': 'Update-Fehler',
  'softwareupdate.beta-enrollment': 'Beta-Programm',
  'passcode.is-present': 'Code vorhanden',
  'passcode.is-compliant': 'Code konform',
  'device.power.battery-health': 'Akkuzustand',
  'package.list': 'Pakete (DDM)',
  'app.managed.list': 'Verwaltete Apps',
  'mdm.app': 'MDM-Apps',
};

const PACKAGE_STATE_LABELS = {
  optional: 'optional',
  queued: 'in Warteschlange',
  'prompting-for-consent': 'wartet auf Zustimmung',
  downloading: 'lädt',
  installing: 'installiert',
  installed: 'installiert',
  failed: 'fehlgeschlagen',
};

const INSTALL_STATE_LABELS = {
  none: 'kein Update',
  waiting: 'wartet',
  downloading: 'lädt',
  prepared: 'bereit',
  installing: 'installiert',
  failed: 'fehlgeschlagen',
};

function pickStatusValues(summary, udid) {
  const raw = summary?.statusValues;
  if (!raw || typeof raw !== 'object' || Array.isArray(raw)) return {};
  if (udid && raw[udid] && typeof raw[udid] === 'object' && !Array.isArray(raw[udid])) return raw[udid];
  return raw;
}

function formatStatusValue(key, value) {
  if (value == null || value === '') return null;
  if (typeof value === 'boolean') return value ? 'ja' : 'nein';
  if (key === 'softwareupdate.install-state') {
    return INSTALL_STATE_LABELS[String(value)] || String(value);
  }
  if (Array.isArray(value)) {
    if (!value.length) return 'keine';
    return value
      .map((item) => {
        if (item == null || typeof item !== 'object') return String(item);
        return item.name || item.identifier || item.Name || JSON.stringify(item);
      })
      .join(', ');
  }
  if (typeof value === 'object') {
    const os = value.OSVersion || value.osVersion || value['OSVersion'];
    const build = value.BuildVersion || value.buildVersion;
    if (os || build) return [os, build].filter(Boolean).join(' · ');
    const reason = value['softwareupdate-reason'] || value.reason || value.Reason;
    if (reason) return String(reason);
    const count = value.count ?? value.Count;
    const code = value.code || value.Code || value.failureReason;
    if (count != null || code) {
      return [count != null ? `${count}×` : null, code, value.timestamp || value.Timestamp]
        .filter(Boolean)
        .join(' · ');
    }
    try {
      return JSON.stringify(value);
    } catch {
      return String(value);
    }
  }
  return String(value);
}

function appRowsFromStatus(values) {
  const lists = [values['mdm.app'], values['app.managed.list']].filter((item) => Array.isArray(item) && item.length);
  const rows = [];
  for (const list of lists) {
    for (const app of list) {
      if (!app || typeof app !== 'object') continue;
      rows.push({
        identifier: app.identifier || app.Identifier || '',
        name: app.name || app.Name || app.identifier || 'App',
        version: app['short-version'] || app.version || app.Version || '',
        state: app.state || app.State || '',
      });
    }
  }
  return rows;
}

/** StatusPackageList (`package.list`) — Install-/Fehlerstatus deklarativer Pakete. */
function packageRowsFromStatus(values) {
  const list = values['package.list'];
  if (!Array.isArray(list) || !list.length) return [];
  return list
    .filter((item) => item && typeof item === 'object' && !item._removed)
    .map((pkg) => {
      const reasons = asList(pkg.reasons).filter(Boolean);
      const reasonText = reasons
        .map((item) => {
          if (typeof item === 'string') return item;
          const code = item.code || item.Code || '';
          const ts = item.details?.Timestamp || item.details?.timestamp || '';
          const desc = item.description || item.Description || '';
          return [code, desc, ts].filter(Boolean).join(' · ');
        })
        .filter(Boolean)
        .join('; ');
      const state = String(pkg.state || pkg.State || '');
      return {
        identifier: pkg.identifier || pkg.Identifier || '',
        declarationId: pkg['declaration-identifier'] || pkg.declarationIdentifier || '',
        name: pkg.name || pkg.Name || pkg.identifier || 'Paket',
        version: pkg.version || pkg.Version || '',
        state,
        stateLabel: PACKAGE_STATE_LABELS[state] || state || '—',
        reasons: reasonText || '—',
      };
    });
}

export default function DdmStatusTable({ udid }) {
  const admin = isAdministrator();
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [busy, setBusy] = useState(false);
  const [unavailable, setUnavailable] = useState(false);
  const [error, setError] = useState(null);
  const [summary, setSummary] = useState(null);

  const load = useCallback(async ({ silent = false } = {}) => {
    if (!udid) {
      setError('Keine UDID');
      setLoading(false);
      return;
    }
    if (!silent) setLoading(true);
    else setRefreshing(true);
    setError(null);
    try {
      const status = await getDdmStatus();
      if (!status?.available) {
        setUnavailable(true);
        setSummary(null);
        return;
      }
      setUnavailable(false);
      const data = await getEnrollmentDdmSummary(udid);
      setSummary(data);
    } catch (err) {
      const statusCode = err.response?.status;
      const message = err.response?.data?.message || err.message || 'DDM-Status konnte nicht geladen werden';
      if (statusCode === 404 && /nicht konfiguriert/i.test(message)) {
        setUnavailable(true);
        setSummary(null);
      } else {
        setError(message);
      }
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, [udid]);

  useEffect(() => {
    load();
  }, [load]);

  const sets = useMemo(() => asList(summary?.sets).filter(Boolean), [summary]);
  const reported = useMemo(() => pickDeclarations(summary, udid), [summary, udid]);
  const errors = useMemo(() => pickErrors(summary, udid), [summary, udid]);
  const rows = useMemo(() => {
    const byId = new Map();
    for (const identifier of asList(summary?.desiredIdentifiers)) {
      byId.set(String(identifier), { identifier: String(identifier), pending: true });
    }
    for (const decl of reported) {
      const identifier = String(decl.identifier || decl.Identifier || '');
      if (!identifier) continue;
      byId.set(identifier, { ...decl, identifier, pending: false });
    }
    return [...byId.values()];
  }, [summary, reported]);

  const enableDdm = async () => {
    setBusy(true);
    try {
      await enableEnrollmentDdm(udid);
      toast.success('DDM-Aktivierung gesendet. Status erscheint nach dem nächsten Check-in.');
      await load({ silent: true });
    } catch (err) {
      toast.error(err.response?.data?.message || err.message || 'DDM konnte nicht aktiviert werden.');
    } finally {
      setBusy(false);
    }
  };

  const notifyDevice = async () => {
    setBusy(true);
    try {
      await notifyEnrollmentDdm(udid);
      toast.success('DDM-Sync angestoßen.');
      await load({ silent: true });
    } catch (err) {
      toast.error(err.response?.data?.message || err.message || 'Notify fehlgeschlagen.');
    } finally {
      setBusy(false);
    }
  };

  const clearErrors = async () => {
    if (!window.confirm('Gespeicherte DDM-Statusfehler für dieses Gerät löschen?')) return;
    setBusy(true);
    try {
      const result = await clearEnrollmentStatusErrors(udid);
      toast.success(`Error-Log geleert (${result?.removed ?? 0} Einträge).`);
      await load({ silent: true });
    } catch (err) {
      toast.error(err.response?.data?.message || err.message || 'Error-Log konnte nicht geleert werden.');
    } finally {
      setBusy(false);
    }
  };

  if (loading) return <Spinner />;

  if (unavailable) {
    return (
      <p className="text-muted mt-3">
        DDM ist auf diesem NanoHUB nicht konfiguriert oder nicht erreichbar.
      </p>
    );
  }

  const lastReport = summary?.lastStatusReport?.lastModified;
  const statusValues = pickStatusValues(summary, udid);

  return (
    <div className="mt-3">
      <div className="d-flex justify-content-between align-items-start flex-wrap gap-2 mb-3">
        <div>
          <h6 className="mb-1">Declarative Device Management</h6>
          <p className="text-muted small mb-0">
            Der Status kommt vom Gerät (DDM-Check-in), nicht vom Speichern einer Deklaration.
            „Sync anstoßen“ aktualisiert auch die Status-Abonnements. Ohne zugewiesenes Set sendet NanoHUB keinen{' '}
            <code>DeclarativeManagement</code>-Befehl.
          </p>
        </div>
        <div className="d-flex gap-2">
          {admin && sets.length === 0 && (
            <button
              type="button"
              className="btn btn-primary btn-sm"
              disabled={busy}
              onClick={enableDdm}
            >
              DDM aktivieren
            </button>
          )}
          {admin && sets.length > 0 && (
            <button
              type="button"
              className="btn btn-outline-primary btn-sm"
              disabled={busy}
              onClick={notifyDevice}
            >
              Sync anstoßen
            </button>
          )}
          <button
            type="button"
            className="btn btn-outline-secondary btn-sm"
            onClick={() => load({ silent: true })}
            disabled={refreshing}
            title="DDM-Status neu laden"
          >
            <FaSync className={refreshing ? 'spin' : ''} />
          </button>
        </div>
      </div>

      {error && (
        <div className="alert alert-warning" role="alert">
          {error}
        </div>
      )}

      <p className="mb-2">
        <strong>Zugewiesene Sets:</strong>{' '}
        {sets.length ? (
          sets.map((name) => (
            <span key={name} className="badge text-bg-light border me-1">
              {name}
            </span>
          ))
        ) : (
          <span className="text-muted">keine — DDM ist für dieses Gerät noch nicht eingeschaltet</span>
        )}
      </p>
      <p className="small text-muted mb-2">
        Letzter Statusbericht:{' '}
        {lastReport ? formatTimestamp(lastReport) : 'noch keiner. Nach der Aktivierung prüft das Gerät und meldet zurück.'}
      </p>
      <p className="small text-muted mb-3">
        {summary?.heartbeat?.note
          || 'Apple sendet Statusreports bei Änderung abonnierter Werte und mindestens alle 24 Stunden.'}
        {' '}Das ist kein Inventar-Polling und ersetzt kein klassisches DeviceInfo/AppInventory.
      </p>

      <SubscriptionPanel subscription={summary?.subscription} subscribed={summary?.subscribedStatusItems} />

      <StatusValuesPanel values={statusValues} />

      {errors.length > 0 && (
        <div className="alert alert-danger" role="alert">
          <div className="d-flex justify-content-between align-items-start gap-2 flex-wrap">
            <strong>Statusfehler ({errors.length})</strong>
            {admin ? (
              <button
                type="button"
                className="btn btn-sm btn-outline-danger"
                disabled={busy}
                onClick={clearErrors}
              >
                Error-Log leeren
              </button>
            ) : null}
          </div>
          <ul className="mb-0 mt-2">
            {errors.map((item, index) => (
              <li key={`${item.path || 'err'}-${index}`}>
                {item.path ? <code className="me-1">{item.path}</code> : null}
                {errorText(item.error)}
              </li>
            ))}
          </ul>
        </div>
      )}

      <table className="table tableList">
        <thead>
          <tr>
            <th>Deklaration</th>
            <th>Status</th>
            <th>Gültig</th>
            <th>Aktiv</th>
            <th>Aktuell</th>
            <th>Gemeldet</th>
            <th>Hinweise</th>
          </tr>
        </thead>
        <tbody>
          {rows.length === 0 ? (
            <tr>
              <td colSpan={7} className="text-muted">
                {sets.length
                  ? 'Sets sind zugewiesen, aber das Gerät hat noch keinen Declaration-Status gemeldet. „Sync anstoßen“ sendet erneut DeclarativeManagement.'
                  : 'Noch keine DDM-Zuweisung. Administratoren können DDM hier aktivieren; iPhone/iPad bekommen DDM zusätzlich, sobald Apps per Gruppe zugewiesen sind.'}
              </td>
            </tr>
          ) : (
            rows.map((decl, index) => {
              const identifier = decl.identifier || `decl-${index}`;
              const badge = syncBadge(decl);
              return (
                <tr key={identifier}>
                  <td><code>{identifier}</code></td>
                  <td>
                    <span className={`badge ${badge.className}`}>{badge.label}</span>
                  </td>
                  <td>{decl.pending ? '—' : (decl.valid || '—')}</td>
                  <td>{decl.pending || decl.active == null ? '—' : decl.active ? 'ja' : 'nein'}</td>
                  <td>{decl.pending || decl.current == null ? '—' : decl.current ? 'ja' : 'nein'}</td>
                  <td>{decl.pending ? '—' : formatTimestamp(decl.status_received || decl.statusReceived)}</td>
                  <td className="text-break">{decl.pending ? 'Noch nicht vom Gerät gemeldet' : formatReasons(decl.reasons)}</td>
                </tr>
              );
            })
          )}
        </tbody>
      </table>
    </div>
  );
}

function SubscriptionPanel({ subscription, subscribed }) {
  const active = asList(subscription?.subscribed?.length ? subscription.subscribed : subscribed).filter(Boolean);
  const skipped = asList(subscription?.skipped);
  const family = subscription?.family;
  const osVersion = subscription?.osVersion;
  if (!active.length && !skipped.length) {
    return (
      <p className="small text-muted mb-3">
        Status-Abonnements werden automatisch vom MDM gesetzt (nicht über den DDM-Editor).
      </p>
    );
  }
  return (
    <div className="mb-4">
      <h6 className="mb-1">Status-Abonnements (automatisch)</h6>
      <p className="small text-muted mb-2">
        Xavier setzt pro Gerät <code>com.apple.configuration.management.status-subscriptions</code>.
        Nicht jedes Topic ist auf jeder Plattform/OS-Version verfügbar
        {family || osVersion ? (
          <>
            {' '}(dieses Gerät: {[family, osVersion].filter(Boolean).join(' · ')})
          </>
        ) : null}.
      </p>
      <div className="mb-2">
        {active.map((name) => (
          <span key={name} className="badge text-bg-success me-1 mb-1" title="abonniert">
            {STATUS_LABELS[name] || name}
          </span>
        ))}
      </div>
      {skipped.length ? (
        <details className="small">
          <summary className="text-muted mb-1">
            {skipped.length} Topic(s) für dieses Gerät übersprungen
          </summary>
          <ul className="mb-0">
            {skipped.map((row) => (
              <li key={row.name || row}>
                <code>{row.name || row}</code>
                {row.reason ? <> — {row.reason}</> : null}
              </li>
            ))}
          </ul>
        </details>
      ) : null}
    </div>
  );
}

function StatusValuesPanel({ values }) {
  const entries = values && typeof values === 'object' ? values : {};
  const groups = STATUS_GROUPS.map((group) => ({
    ...group,
    rows: group.keys
      .map((key) => {
        const formatted = formatStatusValue(key, entries[key]);
        if (formatted == null) return null;
        return { key, label: STATUS_LABELS[key] || key, value: formatted };
      })
      .filter(Boolean),
  })).filter((group) => group.rows.length);
  const apps = appRowsFromStatus(entries);
  const packages = packageRowsFromStatus(entries);

  if (!groups.length && !apps.length && !packages.length) {
    return (
      <p className="small text-muted mb-3">
        Noch keine Statuswerte. Nach dem nächsten DDM-Check-in erscheinen OS-Version, Modell, Update-Status, Apps und Pakete.
      </p>
    );
  }

  return (
    <div className="mb-4">
      <h6 className="mb-2">Gemeldeter Gerätestatus</h6>
      <div className="row g-3">
        {groups.map((group) => (
          <div className="col-12 col-lg-6" key={group.title}>
            <div className="border rounded p-3 h-100 bg-light">
              <div className="fw-semibold mb-2">{group.title}</div>
              <table className="table table-sm mb-0">
                <tbody>
                  {group.rows.map((row) => (
                    <tr key={row.key}>
                      <td className="text-muted" style={{ width: '42%' }}>{row.label}</td>
                      <td className="text-break">{row.value}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        ))}
      </div>
      {packages.length > 0 && (
        <div className="mt-3">
          <div className="fw-semibold mb-2">Pakete (StatusPackageList / <code>package.list</code>)</div>
          <div className="table-responsive">
            <table className="table table-sm align-middle">
              <thead>
                <tr>
                  <th>Name</th>
                  <th>Identifier</th>
                  <th>Deklaration</th>
                  <th>Version</th>
                  <th>Status</th>
                  <th>Gründe</th>
                </tr>
              </thead>
              <tbody>
                {packages.map((pkg, index) => (
                  <tr key={`${pkg.identifier}-${index}`}>
                    <td>{pkg.name}</td>
                    <td><code>{pkg.identifier || '—'}</code></td>
                    <td><code>{pkg.declarationId || '—'}</code></td>
                    <td>{pkg.version || '—'}</td>
                    <td>
                      <span className={`badge ${pkg.state === 'failed' ? 'text-bg-danger' : pkg.state === 'installed' ? 'text-bg-success' : 'text-bg-secondary'}`}>
                        {pkg.stateLabel}
                      </span>
                    </td>
                    <td className="text-break small">{pkg.reasons}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
      {apps.length > 0 && (
        <div className="mt-3">
          <div className="fw-semibold mb-2">Verwaltete Apps (DDM-Status)</div>
          <div className="table-responsive">
            <table className="table table-sm align-middle">
              <thead>
                <tr>
                  <th>App</th>
                  <th>Identifier</th>
                  <th>Version</th>
                  <th>Status</th>
                </tr>
              </thead>
              <tbody>
                {apps.map((app, index) => (
                  <tr key={`${app.identifier}-${index}`}>
                    <td>{app.name}</td>
                    <td><code>{app.identifier || '—'}</code></td>
                    <td>{app.version || '—'}</td>
                    <td>{app.state || '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}
